/** Reglas de selección y construcción del objeto `Selection` que ve el portal. Sin DOM. */
import type { ChartModel } from "./chart-model.js";
import { sumTotal } from "./format.js";
import type { GaSel, Pricing, Selection, SeatSel } from "./types.js";

export function selectedCount(seatCount: number, ga: ReadonlyMap<string, number>): number {
  let n = seatCount;
  for (const q of ga.values()) n += q;
  return n;
}

/** ¿Se pueden añadir `adding` entradas más sin pasar del máximo? */
export const canAdd = (current: number, adding: number, max: number): boolean => current + adding <= max;

/**
 * Máximo que el comprador puede pedir en una zona de pie: lo que queda, y lo que le cabe en su cupo total.
 * `current` es la cantidad que ya tiene en esa misma zona.
 */
export function gaMaxQty(free: number, totalSelected: number, current: number, maxSelectable: number): number {
  const room = maxSelectable - (totalSelected - current);
  return Math.max(0, Math.min(free, room));
}

export function clampQty(qty: number, max: number, min = 0): number {
  if (!Number.isFinite(qty)) return min;
  return Math.max(min, Math.min(max, Math.floor(qty)));
}

export function buildSelection(
  model: ChartModel,
  seatIds: Iterable<string>,
  ga: ReadonlyMap<string, number>,
  pricing: Record<string, Pricing>,
  pending: boolean,
): Selection {
  const seats: SeatSel[] = [];
  const lines: Array<{ price: number | null; currency: string | null; qty: number }> = [];
  for (const id of seatIds) {
    const i = model.index.get(id);
    if (i === undefined) continue;
    const s = model.seats[i]!;
    const row = model.rows[s.row]!;
    const cat = model.cats[s.cat]!;
    const p = pricing[cat.key];
    seats.push({
      id,
      label: s.label,
      rowLabel: row.label,
      sectionLabel: row.sectionLabel,
      category: cat.key,
      categoryLabel: cat.label,
      price: p ? p.price : null,
      currency: p ? p.currency : null,
      accessible: s.accessible,
    });
    lines.push({ price: p ? p.price : null, currency: p ? p.currency : null, qty: 1 });
  }
  const gaSel: GaSel[] = [];
  for (const entry of ga) {
    const zoneId = entry[0];
    const qty = entry[1];
    const zi = model.zoneIndex.get(zoneId);
    if (zi === undefined || qty <= 0) continue;
    const z = model.zones[zi]!;
    const cat = model.cats[z.cat]!;
    const p = pricing[cat.key];
    gaSel.push({ zoneId, label: z.label, qty, category: cat.key, categoryLabel: cat.label, price: p ? p.price : null, currency: p ? p.currency : null });
    lines.push({ price: p ? p.price : null, currency: p ? p.currency : null, qty });
  }
  return { seats, ga: gaSel, total: sumTotal(lines), pending };
}

const ORPHAN_PENALTY = 250;

/**
 * Misma idea que el servidor (`bestAvailable`): `count` butacas contiguas de una fila, lo más cerca posible del
 * escenario y sin dejar una butaca suelta al lado. Solo se usa con `holdOnSelect: false`, donde no hay bloqueo
 * en el servidor que lo calcule. Devuelve índices de butaca, o null si no hay hueco.
 */
export function pickBestLocal(model: ChartModel, isFree: (i: number) => boolean, count: number, category?: string): number[] | null {
  const focal = model.stage ? { x: model.stage.x + model.stage.w / 2, y: model.stage.y + model.stage.h / 2 } : { x: model.width / 2, y: 0 };
  let best: { score: number; ids: number[] } | null = null;
  for (const row of model.rows) {
    if (category && model.cats[row.cat]!.key !== category) continue;
    const free = row.seats.map((i) => isFree(i));
    for (let i = 0; i + count <= row.seats.length; i++) {
      let ok = true;
      for (let j = 0; j < count; j++) {
        if (!free[i + j]) {
          ok = false;
          break;
        }
      }
      if (!ok) continue;
      const win = row.seats.slice(i, i + count).map((n) => model.seats[n]!);
      const cx = win.reduce((a, p) => a + p.x, 0) / count;
      const cy = win.reduce((a, p) => a + p.y, 0) / count;
      let score = Math.hypot(cx - focal.x, cy - focal.y);
      const leftOrphan = i > 0 && free[i - 1] === true && !(i > 1 && free[i - 2] === true);
      const r = i + count;
      const rightOrphan = r < row.seats.length && free[r] === true && !(r + 1 < row.seats.length && free[r + 1] === true);
      if (leftOrphan) score += ORPHAN_PENALTY;
      if (rightOrphan) score += ORPHAN_PENALTY;
      if (!best || score < best.score) best = { score, ids: row.seats.slice(i, i + count) };
    }
  }
  return best ? best.ids : null;
}
